import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { type NostrEvent, NSchema as n } from '@nostrify/nostrify';
import { useNostr } from '@nostrify/react';
import { AuthorBatchContext, type AuthorData } from '@/contexts/AuthorContext';

type Resolver = (data: AuthorData) => void;

const BATCH_WINDOW_MS = 50;
const MAX_AUTHORS_PER_QUERY = 100;

function parseAuthor(event: NostrEvent): AuthorData {
  try {
    const metadata = n.json().pipe(n.metadata()).parse(event.content);
    return { metadata, event };
  } catch {
    return { event };
  }
}

/**
 * Coalesces many concurrent `useAuthor` calls into batched
 * `{ kinds: [0], authors: [...] }` queries (one round-trip per ~100 pubkeys)
 * instead of one query per author. Missing authors resolve as `{}` so callers
 * never retry or hang on pubkeys with no profile event.
 */
export function AuthorProvider({ children }: { children: ReactNode }) {
  const { nostr } = useNostr();
  const pending = useRef(new Map<string, Resolver[]>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(async () => {
    timer.current = null;
    const batch = pending.current;
    pending.current = new Map();
    const pubkeys = [...batch.keys()];
    if (pubkeys.length === 0) return;

    const resolveAll = (results: Map<string, AuthorData>) => {
      for (const [pubkey, resolvers] of batch) {
        const data = results.get(pubkey) ?? {};
        for (const resolve of resolvers) resolve(data);
      }
    };

    try {
      const results = new Map<string, AuthorData>();
      for (let i = 0; i < pubkeys.length; i += MAX_AUTHORS_PER_QUERY) {
        const chunk = pubkeys.slice(i, i + MAX_AUTHORS_PER_QUERY);
        const events = await nostr.query(
          [{ kinds: [0], authors: chunk }],
          { signal: AbortSignal.timeout(3000) },
        );
        for (const event of events) {
          const existing = results.get(event.pubkey)?.event;
          if (!existing || event.created_at > existing.created_at) {
            results.set(event.pubkey, parseAuthor(event));
          }
        }
      }
      resolveAll(results);
    } catch {
      resolveAll(new Map());
    }
  }, [nostr]);

  const load = useCallback((pubkey: string): Promise<AuthorData> => {
    return new Promise<AuthorData>((resolve) => {
      const resolvers = pending.current.get(pubkey);
      if (resolvers) {
        resolvers.push(resolve);
      } else {
        pending.current.set(pubkey, [resolve]);
      }
      if (timer.current === null) {
        timer.current = setTimeout(flush, BATCH_WINDOW_MS);
      }
    });
  }, [flush]);

  const value = useMemo(() => ({ load }), [load]);

  return <AuthorBatchContext.Provider value={value}>{children}</AuthorBatchContext.Provider>;
}
